import { Button, Input, Space } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import React, { useCallback, useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import {
  KVOrientationAxes,
  KVOrientationVector,
  KVSceneSettings,
  RolodexEntryPatchJson,
} from 'types'

const fadeInMini = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.625rem);
  }
  to {
    opacity: 0.95;
    transform: translateY(0);
  }
`

const ControlsContainer = styled.div`
  position: absolute;
  top: 3.75rem;
  right: 1.25rem;
  background: rgba(0, 0, 0, 0.6);
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 14rem;
  width: 18rem;
  animation: ${fadeInMini} 0.3s ease-out forwards;
  opacity: 0.95;
  transition: opacity 0.2s ease;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: default;
`

const ControlLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`

const InstructionText = styled.div`
  font-size: 0.8125rem;
  opacity: 0.85;
  line-height: 1.4;
  margin-bottom: 0.75rem;
`

const StyledButtonRow = styled(Space)`
  width: 100%;
  display: flex;
  gap: 0.5rem;
`

const InputGroup = styled.div`
  margin-bottom: 0.5rem;
`

const InputLabel = styled.label`
  font-size: 0.75rem;
  opacity: 0.8;
  display: block;
  margin-bottom: 0.125rem;
`

const StyledInput = styled(Input)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
`

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

type Step = 'navigate' | 'loading' | 'naming'

export const OrientationAxesPanel: React.FC = () => {
  const {
    sendCommand,
    setOrientationAxesPopoverVisible,
    onOrientationForwardVectorResult,
  } = useKestrelViewerContext()
  const { kestrelModel } = useKestrel()
  const { rolodexPatch } = useRolodexEdit()

  const [step, setStep] = useState<Step>('navigate')
  const [forwardVector, setForwardVector] =
    useState<KVOrientationVector | null>(null)

  // Direction names with defaults (4-letter codes)
  const [nameFront, setNameFront] = useState('FRNT')
  const [nameBack, setNameBack] = useState('BACK')
  const [nameLeft, setNameLeft] = useState('LEFT')
  const [nameRight, setNameRight] = useState('RGHT')
  const [nameUp, setNameUp] = useState('UP')
  const [nameDown, setNameDown] = useState('DOWN')

  // Check if a direction name is valid (1-4 characters)
  const isValidName = (name: string) => {
    const trimmed = name.trim()
    return trimmed.length > 0 && trimmed.length <= 4
  }

  // Check if all direction names are valid
  const allNamesValid =
    isValidName(nameFront) &&
    isValidName(nameBack) &&
    isValidName(nameLeft) &&
    isValidName(nameRight) &&
    isValidName(nameUp) &&
    isValidName(nameDown)

  // Set up callback to receive the forward vector result
  useEffect(() => {
    if (!onOrientationForwardVectorResult) return

    const handleForwardVectorResult = (vector: KVOrientationVector) => {
      setForwardVector(vector)
      setStep('naming')
    }

    onOrientationForwardVectorResult(handleForwardVectorResult)

    return () => {
      onOrientationForwardVectorResult(() => {})
    }
  }, [onOrientationForwardVectorResult])

  const handleConfirmNavigation = useCallback(() => {
    setStep('loading')
    sendCommand({
      command: KestrelCommands.SET_ORIENTATION_FORWARD_VECTOR,
    })
  }, [sendCommand])

  const handleSaveOrientationAxes = useCallback(() => {
    if (!forwardVector || !kestrelModel) return
    if (!allNamesValid) return

    // Create the orientation axes object
    const orientationAxes = new KVOrientationAxes()
    orientationAxes.orientationVector = forwardVector
    orientationAxes.orientationNameFront = nameFront.trim()
    orientationAxes.orientationNameBack = nameBack.trim()
    orientationAxes.orientationNameLeft = nameLeft.trim()
    orientationAxes.orientationNameRight = nameRight.trim()
    orientationAxes.orientationNameUp = nameUp.trim()
    orientationAxes.orientationNameDown = nameDown.trim()

    // Send the command to Kestrel
    sendCommand({
      command: KestrelCommands.SET_ORIENTATION_AXES,
      orientationAxes: KVOrientationAxes.toJSON(orientationAxes),
    })

    // Save to Rolodex
    const sceneSettings: KVSceneSettings =
      kestrelModel?.data.sceneSettings ?? new KVSceneSettings()
    sceneSettings.orientationAxes = orientationAxes

    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: {
        sceneSettings: KVSceneSettings.toJSON(sceneSettings),
      },
    }
    rolodexPatch([patchData])

    // Close the panel
    setOrientationAxesPopoverVisible(false)
  }, [
    forwardVector,
    kestrelModel,
    allNamesValid,
    nameFront,
    nameBack,
    nameLeft,
    nameRight,
    nameUp,
    nameDown,
    sendCommand,
    rolodexPatch,
    setOrientationAxesPopoverVisible,
  ])

  const handleCancel = useCallback(() => {
    setOrientationAxesPopoverVisible(false)
  }, [setOrientationAxesPopoverVisible])

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation()
  }, [])

  if (step === 'loading') {
    return (
      <ControlsContainer
        onClick={stopPropagation}
        onMouseDown={stopPropagation}>
        <ControlLabel>Orientation Axes</ControlLabel>
        <InstructionText>Capturing orientation...</InstructionText>
        <LoadingSpinner />
      </ControlsContainer>
    )
  }

  if (step === 'naming') {
    return (
      <ControlsContainer
        onClick={stopPropagation}
        onMouseDown={stopPropagation}>
        <ControlLabel>Orientation Axes</ControlLabel>
        <InstructionText>
          What would you like to name these directions? (Max 4 characters each)
        </InstructionText>

        <InputGroup>
          <InputLabel>Front</InputLabel>
          <StyledInput
            value={nameFront}
            onChange={(e) => setNameFront(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={stopPropagation}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            placeholder='FRNT'
            maxLength={4}
          />
        </InputGroup>

        <InputGroup>
          <InputLabel>Back</InputLabel>
          <StyledInput
            value={nameBack}
            onChange={(e) => setNameBack(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={stopPropagation}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            placeholder='BACK'
            maxLength={4}
          />
        </InputGroup>

        <InputGroup>
          <InputLabel>Left</InputLabel>
          <StyledInput
            value={nameLeft}
            onChange={(e) => setNameLeft(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={stopPropagation}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            placeholder='LEFT'
            maxLength={4}
          />
        </InputGroup>

        <InputGroup>
          <InputLabel>Right</InputLabel>
          <StyledInput
            value={nameRight}
            onChange={(e) => setNameRight(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={stopPropagation}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            placeholder='RGHT'
            maxLength={4}
          />
        </InputGroup>

        <InputGroup>
          <InputLabel>Up</InputLabel>
          <StyledInput
            value={nameUp}
            onChange={(e) => setNameUp(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={stopPropagation}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            placeholder='UP'
            maxLength={4}
          />
        </InputGroup>

        <InputGroup>
          <InputLabel>Down</InputLabel>
          <StyledInput
            value={nameDown}
            onChange={(e) => setNameDown(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={stopPropagation}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            placeholder='DOWN'
            maxLength={4}
          />
        </InputGroup>

        <StyledButtonRow>
          <Button
            type='primary'
            onClick={handleSaveOrientationAxes}
            style={{ flex: 1 }}
            disabled={!allNamesValid}>
            Save
          </Button>
          <Button onClick={handleCancel} style={{ flex: 1 }}>
            Cancel
          </Button>
        </StyledButtonRow>
      </ControlsContainer>
    )
  }

  // Default: 'navigate' step
  return (
    <ControlsContainer onClick={stopPropagation} onMouseDown={stopPropagation}>
      <ControlLabel>Orientation Axes</ControlLabel>
      <InstructionText>
        Navigate such that you are looking in the forward direction, level with
        your model. Press confirm when complete.
      </InstructionText>
      <StyledButtonRow>
        <Button
          type='primary'
          onClick={handleConfirmNavigation}
          style={{ flex: 1 }}>
          Confirm
        </Button>
        <Button onClick={handleCancel} style={{ flex: 1 }}>
          Cancel
        </Button>
      </StyledButtonRow>
    </ControlsContainer>
  )
}
